import axios from "axios";
import TokenManager from "../utils/tokenManager";

// import { api } from "../config";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// Set authorization header using secure token manager
const setAuthHeader = () => {
  const token = TokenManager.getToken();
  if (token) {
    // Send token without Bearer prefix to match backend expectation
    axios.defaults.headers.common["Authorization"] = token;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

// Don't set auth header during module initialization
// It will be set by the request interceptor when needed
// setAuthHeader();

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data
    });

    // For authentication endpoints, preserve the original error for enhanced handling
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    
    if (isAuthEndpoint) {
      // Clear token on auth errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        TokenManager.clearToken();
      }
      // Return the original error object so our enhanced error handling can access response.data
      return Promise.reject(error);
    }

    // For tournament endpoints, don't clear token on 401/403 as it might be a temporary issue
    const isTournamentEndpoint = error.config?.url?.includes('/tournaments/') || 
                                error.config?.url?.includes('/api/v1/tournaments/');
    
    // For club endpoints, preserve original error for specific error handling
    const isClubEndpoint = error.config?.url?.includes('/club/') || 
                          error.config?.url?.includes('/api/v1/club/');

    // For enrollment endpoints, preserve original error so callers can read response.data
    const isEnrollmentEndpoint = error.config?.url?.includes('/enrollments/');

    // For user admin endpoints (search-profiles, add-profile, etc.), preserve original
    // error so callers can read response.data (e.g. {error: "User not found", statusCode: 404})
    const isUsersAdminEndpoint = error.config?.url?.includes('/users/admin/');

    if (isTournamentEndpoint || isClubEndpoint || isEnrollmentEndpoint || isUsersAdminEndpoint) {
      return Promise.reject(error);
    }

    // For other endpoints, use the existing error handling
    let message;
    switch (error.response?.status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        message = "Invalid credentials";
        // Only clear token on 401 for non-tournament endpoints and not during initial load
        if (!error.config?.url?.includes('/tournaments/') && 
            !error.config?.url?.includes('/api/v1/tournaments/')) {
          // Add a small delay to allow token restoration during page refresh
          setTimeout(() => {
            const token = TokenManager.getToken();
            if (!token) {
              TokenManager.clearToken();
            }
          }, 1000);
        }
        break;
      case 403:
        message = "Access forbidden - Token may be invalid";
        // Only clear token on 403 for non-tournament endpoints and not during initial load
        if (!error.config?.url?.includes('/tournaments/') && 
            !error.config?.url?.includes('/api/v1/tournaments/')) {
          // Add a small delay to allow token restoration during page refresh
          setTimeout(() => {
            const token = TokenManager.getToken();
            if (!token) {
              TokenManager.clearToken();
            }
          }, 1000);
        }
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      default:
        message = error.message || error;
    }
    return Promise.reject(message);
  }
);

// Add request interceptor for debugging
axios.interceptors.request.use(
  function (config) {
    // Set auth header for each request
    setAuthHeader();
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: any) => {
  if (token) {
    TokenManager.setToken(token);
  }
  setAuthHeader();
};

class APIClient {
  /**
   * Fetches data from given url
   */

  //  get = (url, params) => {
  //   return axios.get(url, params);
  // };
  get = (url: any, params: any) => {
    setAuthHeader(); // Ensure auth header is set before request
    let response;

    let paramKeys: any = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`);
    } else {
      response = axios.get(`${url}`);
    }

    return response;
  };
  /**
   * post given data to url
   */
  create = (url: any, data: any) => {
    setAuthHeader(); // Ensure auth header is set before request
    return axios.post(url, data);
  };
  /**
   * Updates data
   */
  update = (url: any, data: any) => {
    setAuthHeader(); // Ensure auth header is set before request
    return axios.patch(url, data);
  };

  put = (url: any, data: any) => {
    setAuthHeader(); // Ensure auth header is set before request
    return axios.put(url, data);
  };
  /**
   * Delete
   */
  delete = (url: any, config: any) => {
    setAuthHeader(); // Ensure auth header is set before request
    return axios.delete(url, { ...config });
  };
}
const getLoggedinUser = () => {
  return TokenManager.getUserData();
};

export { APIClient, setAuthorization, getLoggedinUser };